import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Grid,
  LinearProgress,
  Stack
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';

import { BodyText, CaptionText, MinorHeading } from '../../shared/typography/PrimaryTypography';
import { EsgRecord } from '../../../types/syntiro_honey';
import ConfidenceBandChip from './ConfidenceBandChip';
import MappingStatusChip from './MappingStatusChip';
import RecordStatusChip from './RecordStatusChip';
import { describeValidationFlag, isMappedFromLearnedRule } from './validation_flags';
import { formatProvenance } from './provenance';
import { resolveRecordEntity } from './record_entity';
import { formatDisplayValue } from './value_format';

interface SummaryFieldProps {
  /** Field label. */
  label: string;
  /** Rendered value. */
  children: React.ReactNode;
}

/** Labelled read-only field used throughout the summary grid. */
function SummaryField({ label, children }: SummaryFieldProps) {
  return (
    <Grid size={6}>
      <CaptionText>{label}</CaptionText>
      <BodyText sx={{ wordBreak: 'break-word' }}>{children}</BodyText>
    </Grid>
  );
}

/** Format a nullable confidence score as a whole-number percentage (0.72 → "72%"). */
function formatConfidence(confidence: number | null): string {
  return confidence === null ? '—' : `${Math.round(confidence * 100)}%`;
}

/** Joins the non-empty parts of a nullable list with a separator, or "—" when all empty. */
function joinParts(parts: Array<string | null>, separator: string): string {
  const present = parts.filter((part): part is string => part !== null && part.trim() !== '');
  return present.length > 0 ? present.join(separator) : '—';
}

interface ConfidenceBarProps {
  /** Sub-score label (which pipeline dimension this measures). */
  label: string;
  /** The sub-score in [0, 1], or null when not computed. */
  score: number | null;
}

/** One labelled confidence sub-score as a mini progress bar + number. */
function ConfidenceBar({ label, score }: ConfidenceBarProps) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <CaptionText sx={{ width: 56, flexShrink: 0 }}>{label}</CaptionText>
      <LinearProgress
        variant="determinate"
        value={(score ?? 0) * 100}
        color={
          score !== null && score >= 0.9
            ? 'success'
            : score !== null && score >= 0.7
              ? 'warning'
              : 'error'
        }
        sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
      />
      <CaptionText sx={{ width: 36, textAlign: 'right' }}>{formatConfidence(score)}</CaptionText>
    </Stack>
  );
}

interface RecordDetailSummaryProps {
  /** The record being inspected. */
  record: EsgRecord;
}

/**
 * Read-only summary of a staged record: extracted vs normalised values,
 * per-field confidences, status + markers, validation flags and the original
 * extraction payload (collapsible, audit provenance).
 * @returns {JSX.Element}
 */
export default function RecordDetailSummary({ record }: RecordDetailSummaryProps) {
  const provenanceLine = formatProvenance(record.rawPayload);
  const resolvedEntity = resolveRecordEntity(record);
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <RecordStatusChip
          status={record.status}
          approvedById={record.approvedById}
          approvedAt={record.approvedAt}
          approvedByName={record.approvedByName}
        />
        <MappingStatusChip
          status={record.mappingStatus}
          approved={record.status === 'APPROVED'}
          learned={isMappedFromLearnedRule(record.validationFlags)}
        />
        <ConfidenceBandChip
          confidence={record.overallConfidence}
          approved={record.status === 'APPROVED'}
        />
      </Stack>
      {/* Two SEPARATE trust signals, led with the value so a correct number is never
          buried under an uncertain mapping. The trusted copy must not name a specific
          culprit (mapping/unit) — it was wrong whenever the OTHER one was the problem
          (Maxine R5 ①). */}
      <Alert severity={record.valueTrusted ? 'success' : 'warning'} variant="outlined">
        {record.valueTrusted
          ? 'Value read exactly from the source cell — trusted. Please still check the mapping details below.'
          : 'Value could not be verified from the source — please check it against the document.'}
      </Alert>
      {record.unitNudge !== null && (
        <Alert severity="info" variant="outlined">
          Unit may need a correction — {record.unitNudge}
        </Alert>
      )}
      {record.supersedesPrior != null && (
        // This record REPLACES an earlier one (an amendment, or a duplicate UPSERT
        // resolution): approving it makes the new value the live one, so the
        // old → new change must be visible exactly here (Maxine 2026-08-19 S2②).
        <Alert severity="info" variant="outlined">
          {`Value changes from ${record.supersedesPrior.complianceValue ?? formatDisplayValue(record.supersedesPrior.value)} ${record.supersedesPrior.canonicalUnit ?? ''} to ${record.complianceValue ?? formatDisplayValue(record.value)} ${record.canonicalUnit ?? ''} — replaces the value previously in "${record.supersedesPrior.fileName}".`}
        </Alert>
      )}
      {provenanceLine !== null && (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <PlaceOutlinedIcon fontSize="small" color="action" />
          <BodyText>{provenanceLine}</BodyText>
        </Stack>
      )}
      <Grid container rowSpacing={1.5} columnSpacing={2}>
        <SummaryField label="Raw label (as extracted)">{record.rawLabel}</SummaryField>
        <SummaryField label="Client value (original)">
          {formatDisplayValue(record.rawValue)} {record.rawUnit ?? ''}
        </SummaryField>
        <SummaryField label="Normalised value">
          {formatDisplayValue(record.value)} {record.canonicalUnit ?? ''}
        </SummaryField>
        <SummaryField label="Qualitative verdict">
          {record.complianceValue ?? '—'}
          {record.qualitativeNote !== null && ` — ${record.qualitativeNote}`}
        </SummaryField>
        <SummaryField label="Metric">
          {record.metricName ?? record.metricId ?? 'unmapped'}
          {record.metricGroup !== null && ` (${record.metricGroup})`}
        </SummaryField>
        <SummaryField label="Entity">
          {/* A company-level record has no org path to join — its name IS the
              company, so the breadcrumb collapses to that (Maxine ⑨). */}
          {resolvedEntity === null
            ? '—'
            : `${
                resolvedEntity.level === 'Company'
                  ? resolvedEntity.name
                  : joinParts([record.divisionName, record.subdivisionName, record.siteName], ' › ')
              } (${resolvedEntity.level})`}
        </SummaryField>
        <SummaryField label="Country / province">
          {joinParts([record.countryName, record.provinceName], ' · ')}
        </SummaryField>
        <SummaryField label="Reporting period">{record.periodLabel ?? '—'}</SummaryField>
        <SummaryField label="Assignee">
          {record.assignedToName ?? record.assignedToId ?? '—'}
        </SummaryField>
        <SummaryField label="Created">{new Date(record.createdAt).toLocaleString()}</SummaryField>
        <SummaryField label="Source file">{record.fileName}</SummaryField>
        <SummaryField label="Metric-list version">{record.metricListVersionLabel}</SummaryField>
      </Grid>
      {/* Legacy blended sub-scores, collapsed so the two honest signals above lead
          (and a "value 60%" bar doesn't visually contradict the value-trust banner). */}
      <Accordion disableGutters variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <CaptionText weight="Medium">Confidence sub-scores (system-computed)</CaptionText>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={0.75}>
            <ConfidenceBar label="metric" score={record.metricConfidence} />
            <ConfidenceBar label="unit" score={record.unitConfidence} />
            <ConfidenceBar label="value" score={record.valueConfidence} />
          </Stack>
        </AccordionDetails>
      </Accordion>
      {record.validationFlags !== null && record.validationFlags.length > 0 && (
        <Stack spacing={1}>
          <MinorHeading weight="Medium">Validation flags</MinorHeading>
          {record.validationFlags.map((flag) => {
            const { title, description, severity } = describeValidationFlag(flag);
            return (
              <Alert key={flag} severity={severity} variant="outlined">
                <AlertTitle sx={{ mb: 0 }}>{title}</AlertTitle>
                {description}
              </Alert>
            );
          })}
        </Stack>
      )}
      {record.note !== null && (
        <Stack spacing={0.5}>
          <CaptionText>Note</CaptionText>
          <BodyText>{record.note}</BodyText>
        </Stack>
      )}
      {/* The original extraction payload (provenance) now lives in the detail
          modal's History tab, so the summary stays focused on the data fields. */}
    </Stack>
  );
}
